import { ChevronRight } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/shared/components/EmptyState";
import { MarketplaceChip } from "@/shared/components/MarketplaceChip";

import type { SplitGroup } from "../lib/types";

import { StatusBadge } from "./StatusBadge";

interface SplitTableProps {
  splits: SplitGroup[];
  onOpen: (saleOrderId: string) => void;
}

export function SplitTable({ splits, onOpen }: SplitTableProps): JSX.Element {
  const { t } = useTranslation("orders");

  if (splits.length === 0) {
    return (
      <EmptyState
        title={t("empty_no_orders")}
        body={t("empty_no_orders_desc")}
        data-testid="splits-empty"
      />
    );
  }

  return (
    <div
      className="overflow-x-auto rounded-[14px] border border-[var(--premium-border)] shadow-[var(--sh-card)]"
      data-testid="splits-table"
    >
      <table className="min-w-full border-collapse">
        <thead className="sticky top-0 z-20 bg-white border-b border-[var(--row-divider)]">
          <tr>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_sale_order")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_marketplace")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_created")}
            </th>
            <th className="h-[52px] px-4 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_split_count")}
            </th>
            <th className="h-[52px] px-4 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_labels")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_status")}
            </th>
            <th className="h-[52px] px-4">
              <span className="sr-only">{t("th_actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--row-divider)]">
          {splits.map((s) => (
            <tr
              key={s.sale}
              data-testid="split-row"
              className="group cursor-pointer transition-colors hover:bg-[var(--row-hover)]"
              onClick={() => onOpen(s.sale)}
            >
              {/* Sale order ID */}
              <td className="h-[52px] px-4 text-sm text-[var(--text-primary)] whitespace-nowrap">
                <span className="font-mono text-[12px] font-semibold">{s.sale}</span>
              </td>

              {/* Marketplace */}
              <td className="h-[52px] px-4 whitespace-nowrap">
                <MarketplaceChip marketplace={s.mkp} />
              </td>

              {/* Created */}
              <td className="h-[52px] px-4 text-sm text-[var(--text-secondary)] whitespace-nowrap tabular-nums">
                {s.date}
              </td>

              {/* Split count */}
              <td className="h-[52px] px-4 text-right text-sm tabular-nums text-[var(--text-primary)] whitespace-nowrap">
                {s.count}
              </td>

              {/* Labels */}
              <td className="h-[52px] px-4 text-right text-sm tabular-nums text-[var(--text-primary)] whitespace-nowrap">
                {s.count}
              </td>

              {/* Status */}
              <td className="h-[52px] px-4 whitespace-nowrap">
                <StatusBadge status={s.status} />
              </td>

              {/* Action */}
              <td className="h-[52px] px-4 whitespace-nowrap">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(s.sale);
                  }}
                  aria-label={t("act_view")}
                  className="grid h-8 w-8 place-items-center rounded-lg text-[var(--text-subtle)] hover:bg-white hover:text-[var(--text-primary)] hover:shadow-[var(--sh-xs)] hover:border hover:border-[var(--premium-border-strong)] focus-ring"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
